import { Flex, Text } from '@chakra-ui/react'
import { BloodPressure, BloodPressureField } from '@hb/shared'
import { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react'
import { InputElement, InputProps } from '../../../../types/fields'
import { BaseNumberInput } from '../Number/BaseNumberInput'
import { useInputSize } from '../Shared/sizes'

export const BloodPressureInput: InputElement<BloodPressureField> = forwardRef<
  HTMLInputElement,
  InputProps<BloodPressureField>
>((props, ref) => {
  const { input, field, disabled, readOnly, meta } = props
  const size = useInputSize(props.size ?? field?.size)
  const { optional } = field

  const value = useMemo<Partial<BloodPressure>>(
    () => (typeof input.value === 'object' && input.value ? input.value : {}),
    [input.value],
  )

  const systolicRef = useRef<string>(value.systolic !== undefined ? String(value.systolic) : '')
  const diastolicRef = useRef<string>(value.diastolic !== undefined ? String(value.diastolic) : '')
  // const [systolicString, setSystolicString] = useState(systolicRef.current)
  // const [diastolicString, setDiastolicString] = useState(diastolicRef.current)

  useEffect(() => {
    if (systolicRef.current || diastolicRef.current) return
    if (!value.systolic && !value.diastolic) return
    systolicRef.current = value.systolic !== undefined ? String(value.systolic) : ''
    diastolicRef.current = value.diastolic !== undefined ? String(value.diastolic) : ''
  }, [value])

  const handleChange = useCallback(() => {
    const sys = Number(systolicRef.current)
    const dia = Number(diastolicRef.current)
    if (isNaN(sys) || isNaN(dia)) {
      input.onChange(undefined)
      return
    }
    input.onChange({
      systolic: isNaN(sys) ? undefined : sys,
      diastolic: isNaN(dia) ? undefined : dia,
    })
  }, [input])

  return (
    <Flex width={field.row ? '100%' : undefined} gap={2} alignItems="center">
      <BaseNumberInput
        field={field}
        input={input}
        meta={meta}
        disabled={disabled}
        placeholder="Systolic"
        readOnly={readOnly}
        onChange={asString => {
          systolicRef.current = asString
          handleChange()
        }}
        value={systolicRef.current}
        style={{ width: field.row ? 'auto' : '100px', flex: field.row ? 1 : undefined }}
        min={0}
        max={300}
        ref={ref}
        optional={optional}
        step={1}
        precision={0}
      />

      <Text
        fontSize={size === 'xs' ? 'md' : size === 'sm' ? 'lg' : size === 'lg' ? '2xl' : 'xl'}
        fontWeight={600}
        color="gray.500">
        /
      </Text>
      <BaseNumberInput
        field={field}
        meta={meta}
        input={input}
        disabled={disabled}
        placeholder="Diastolic"
        readOnly={readOnly}
        onChange={asString => {
          diastolicRef.current = asString
          handleChange()
        }}
        value={diastolicRef.current}
        min={0}
        max={200}
        style={{ width: field.row ? 'auto' : '100px', flex: field.row ? 1 : undefined }}
        optional={optional}
        step={1}
        precision={0}
      />
    </Flex>
  )
})
